import React, { useState } from "react";
import {
  Modal,
  Steps,
  Button,
  Image,
  Table,
  Checkbox,
  Upload,
  Radio
} from "antd";
import "react-datepicker/dist/react-datepicker.css";
import { UploadOutlined, DeleteOutlined } from "@ant-design/icons";
import { X } from "lucide-react";
import Select from "react-select";
import moment from "moment";
import Swal from "sweetalert2";
import PropTypes from "prop-types";
import { AiFillFilePdf } from "react-icons/ai";
import { FaFileWord } from "react-icons/fa";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import axios from "axios";
const { Step } = Steps;



const EmployeeModal = ({
  isOpen,
  onClose,
  isEdit,
  formData,
  setFormData,
  onSubmit,
  loading,
  // Props for dropdown options
  titleOptions,
  gender,
  branchOptions,
  departments,
  designations,
  shifts,
  nationalitiesOptions,
  employeeCategoryOptions,
  employeeTypes,
  payFrequencies,
  sriLankaProvinces,
  education_and_training,
  // Other props
  uploadedPic,
  setUploadedPic,
  documents,
  setDocuments,
  hasShiftChanged,
  setHasShiftChanged,
  record,
  center,
  setFileList,
  // Compensation props
  allowances,
  deductionTypes,
  compensationData,
  setCompensationData,
  onRefreshAllowanceCategories,
  onRefreshDeductionTypes,
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [errors, setErrors] = useState({});
  const checkTimeoutRef = React.useRef(null);

  React.useEffect(() => {
    return () => {
      if (checkTimeoutRef.current) {
        clearTimeout(checkTimeoutRef.current);
      }
    };
  }, []);

  const checkEpfDuplicate = (epf) => {
    if (checkTimeoutRef.current) {
      clearTimeout(checkTimeoutRef.current);
    }

    checkTimeoutRef.current = setTimeout(async () => {
      try {
        const excludeId = isEdit && record ? record._id : null;
        const response = await axios.get("/employees/check-epf", {
          params: { epfNumber: epf, excludeId }
        });
        if (response.data && response.data.isDuplicate) {
          setErrors((prev) => ({
            ...prev,
            epfNumber: "This EPF number already exists.",
          }));
        } else {
          setErrors((prev) => {
            const newErrors = { ...prev };
            delete newErrors.epfNumber;
            return newErrors;
          });
        }
      } catch (error) {
        console.error("Error during EPF duplicate validation:", error);
      }
    }, 400);
  };

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);

  // Compensation - Allowance form state
  const [allowanceCategory, setAllowanceCategory] = useState(null);
  const [allowanceAmount, setAllowanceAmount] = useState("0.00");
  const [allowanceDate, setAllowanceDate] = useState(null);
  const [editingAllowanceIndex, setEditingAllowanceIndex] = useState(-1);

  // Compensation - Deduction form state
  const [deductionType, setDeductionType] = useState(null);
  const [deductionAmount, setDeductionAmount] = useState("0.00");
  const [deductionDate, setDeductionDate] = useState(null);
  const [deductionPaymentType, setDeductionPaymentType] = useState("oneTime");
  const [editingDeductionIndex, setEditingDeductionIndex] = useState(-1);

  // Inline category creation
  const [showNewAllowanceCat, setShowNewAllowanceCat] = useState(false);
  const [newAllowanceCatName, setNewAllowanceCatName] = useState("");
  const [newAllowanceCatType, setNewAllowanceCatType] = useState("Fixed");
  const [showNewDeductionCat, setShowNewDeductionCat] = useState(false);
  const [newDeductionCatName, setNewDeductionCatName] = useState("");

  // Compensation helper functions
  const showCompSuccess = (str) => {
    Swal.fire({ title: "Success", text: str, icon: "success", timer: 1500 });
  };

  const resetAllowanceForm = () => {
    setAllowanceCategory(null);
    setAllowanceAmount("0.00");
    setAllowanceDate(null);
    setEditingAllowanceIndex(-1);
  };

  const resetDeductionForm = () => {
    setDeductionType(null);
    setDeductionAmount("0.00");
    setDeductionDate(null);
    setDeductionPaymentType("oneTime");
    setEditingDeductionIndex(-1);
  };

  const handleAddAllowance = () => {
    if (!allowanceCategory) {
      Swal.fire({ title: "Error", text: "Please select an Allowance Category", icon: "error" });
      return;
    }
    const amt = parseFloat(String(allowanceAmount).replace(/,/g, ""));
    if (isNaN(amt) || amt <= 0) {
      Swal.fire({ title: "Error", text: "Amount must be greater than 0", icon: "error" });
      return;
    }
    if (!allowanceDate) {
      Swal.fire({ title: "Error", text: "Please select an Effective Date", icon: "error" });
      return;
    }

    const newItem = {
      allowanceTypeId: allowanceCategory.value,
      allowanceTypeName: allowanceCategory.label,
      amount: amt,
      effectiveDate: moment(allowanceDate).format("YYYY-MM-DD"),
    };

    const updated = { ...compensationData };
    if (editingAllowanceIndex >= 0) {
      updated.allowances[editingAllowanceIndex] = newItem;
    } else {
      updated.allowances = [...(updated.allowances || []), newItem];
    }
    setCompensationData(updated);
    resetAllowanceForm();
  };

  const handleEditAllowance = (index) => {
    const item = compensationData.allowances[index];
    setAllowanceCategory({ value: item.allowanceTypeId, label: item.allowanceTypeName });
    setAllowanceAmount(item.amount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
    setAllowanceDate(new Date(item.effectiveDate));
    setEditingAllowanceIndex(index);
  };

  const handleDeleteAllowance = (index) => {
    const updated = { ...compensationData };
    updated.allowances = updated.allowances.filter((_, i) => i !== index);
    setCompensationData(updated);
  };

  const handleAddDeduction = () => {
    if (!deductionType) {
      Swal.fire({ title: "Error", text: "Please select a Deduction Type", icon: "error" });
      return;
    }
    const amt = parseFloat(String(deductionAmount).replace(/,/g, ""));
    if (isNaN(amt) || amt <= 0) {
      Swal.fire({ title: "Error", text: "Amount must be greater than 0", icon: "error" });
      return;
    }
    if (!deductionDate) {
      Swal.fire({ title: "Error", text: "Please select an Effective Date", icon: "error" });
      return;
    }

    const newItem = {
      deductionTypeId: deductionType.value,
      deductionTypeName: deductionType.label,
      amount: amt,
      effectiveDate: moment(deductionDate).format("YYYY-MM-DD"),
      paymentType: deductionPaymentType,
    };

    const updated = { ...compensationData };
    if (editingDeductionIndex >= 0) {
      updated.deductions[editingDeductionIndex] = newItem;
    } else {
      updated.deductions = [...(updated.deductions || []), newItem];
    }
    setCompensationData(updated);
    resetDeductionForm();
  };

  const handleEditDeduction = (index) => {
    const item = compensationData.deductions[index];
    setDeductionType({ value: item.deductionTypeId, label: item.deductionTypeName });
    setDeductionAmount(item.amount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
    setDeductionDate(new Date(item.effectiveDate));
    setDeductionPaymentType(item.paymentType || "oneTime");
    setEditingDeductionIndex(index);
  };

  const handleDeleteDeduction = (index) => {
    const updated = { ...compensationData };
    updated.deductions = updated.deductions.filter((_, i) => i !== index);
    setCompensationData(updated);
  };

  const handleCreateAllowanceCategory = async () => {
    if (!newAllowanceCatName.trim()) {
      Swal.fire({ title: "Error", text: "Category name is required", icon: "error" });
      return;
    }
    try {
      const userId = JSON.parse(localStorage.getItem("user")).data._id;
      await axios.post("/allowances", {
        name: newAllowanceCatName.trim(),
        allowanceType: newAllowanceCatType,
        user: userId,
      });
      showCompSuccess("Allowance Category created successfully");
      setNewAllowanceCatName("");
      setNewAllowanceCatType("Fixed");
      setShowNewAllowanceCat(false);
      if (onRefreshAllowanceCategories) onRefreshAllowanceCategories();
    } catch (err) {
      Swal.fire({ title: "Error", text: err.response?.data?.error || "Failed to create category", icon: "error" });
    }
  };

  const handleCreateDeductionType = async () => {
    if (!newDeductionCatName.trim()) {
      Swal.fire({ title: "Error", text: "Deduction type name is required", icon: "error" });
      return;
    }
    try {
      await axios.post("/deduction-type", {
        deducitionType: newDeductionCatName.trim(),
      });
      showCompSuccess("Deduction Type created successfully");
      setNewDeductionCatName("");
      setShowNewDeductionCat(false);
      if (onRefreshDeductionTypes) onRefreshDeductionTypes();
    } catch (err) {
      Swal.fire({ title: "Error", text: err.response?.data?.error || "Failed to create deduction type", icon: "error" });
    }
  };

  // Validation functions
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateSriLankaPhoneNumber = (phoneNumber) => {
    const sriLankaPhoneRegex = /^0[1-9]\d{8}$/;
    return sriLankaPhoneRegex.test(phoneNumber);
  };

  const showWarning = (str) => {
    return Swal.fire({
      title: "Error",
      text: str,
      icon: "error",
    });
  };



  // NIC Validation
  const validateNIC = (nic) => {
    const oldNicRegex = /^\d{9}[VX]$/;
    const newNicRegex = /^\d{12}$/;
    return oldNicRegex.test(nic) || newNicRegex.test(nic);
  };

  const handleSubmit = () => {
    // Validation logic
    if (errors.epfNumber) {
      showWarning(errors.epfNumber);
      return;
    }
    if (!formData.center) {
      showWarning("Center selection is required. Please choose a center.");
      return;
    }
    if (formData.shift === "") {
      showWarning("Shift selection is required. Please choose a shift.");
      return;
    }
    if (formData.email && !validateEmail(formData.email)) {
      showWarning("Email is not valid!");
      return;
    }
    if (formData.contactNumber && !validateSriLankaPhoneNumber(formData.contactNumber)) {
      showWarning("Phone number is not valid");
      return;
    }
    if (formData.emergencyNo1 && !validateSriLankaPhoneNumber(formData.emergencyNo1)) {
      showWarning("Emergency number 01 is not valid");
      return;
    }
    if (formData.emergencyNo2 && !validateSriLankaPhoneNumber(formData.emergencyNo2)) {
      showWarning("Emergency number 02 is not valid");
      return;
    }
    // NIC Validation Check
    if (formData.nic && !validateNIC(formData.nic)) {
      showWarning("Invalid NIC format. Please enter a valid NIC (e.g., 802001754V or 197818601170).");
      return;
    }

    if (formData.employeeCategory === "Full Time" && (!formData.basicSalary || parseFloat(formData.basicSalary.toString().replace(/,/g, "")) <= 0)) {
      showWarning("Basic Salary is required and must be greater than 0 for Full Time employees.");
      return;
    }
    if (["Contract", "Probation"].includes(formData.employeeCategory)) {
      if (!formData.probationStart) {
        showWarning("Probation Start Date is required for Contract or Probation employees.");
        return;
      }
      if (!formData.probationEnd) {
        showWarning("Probation End Date is required for Contract or Probation employees.");
        return;
      }
      if (moment(formData.probationEnd).isBefore(moment(formData.probationStart))) {
        showWarning("Probation End Date cannot be before Probation Start Date.");
        return;
      }
    }

    // Call parent submit handler
    onSubmit();
  };
  const handleInputChange = (e, field) => {
    let value = e.target.type === "checkbox" ? e.target.checked : e.target.value;

    // NIC Input Handling: allow only digits + V/X (same as editemployee.jsx)
    if (field === "nic") {
      if (value !== "" && !/^[0-9VXvx\s]*$/.test(value)) {
        return; // block invalid characters silently
      }
      value = value.replace(/\s+/g, "").toUpperCase();
    }

    // Letters and spaces only
    if (["full_name", "accountHolderName", "emergency_name", "emergency_relationship"].includes(field)) {
      value = value.replace(/[^a-zA-Z\s]/g, "");
    }

    // Digits only
    if (["epfNumber", "contactNumber", "emergencyNo1", "emergencyNo2", "accountNumber"].includes(field)) {
      value = value.replace(/\D/g, "");
    }

    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    // Clear validation error for this field as the user types
    if (errors[field]) {
      setErrors((prev) => { const e = { ...prev }; delete e[field]; return e; });
    }

    if (field === "epfNumber") {
      if (!value) {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors.epfNumber;
          return newErrors;
        });
      } else {
        checkEpfDuplicate(value);
      }
    }
  };

  const handleDateChange = (date, dateString, field) => {
    setFormData((prev) => ({
      ...prev,
      [field]: dateString || null,
    }));
    if (errors[field]) {
      setErrors((prev) => { const e = { ...prev }; delete e[field]; return e; });
    }
  };

  const handleSelectChange = (selectedOption, name) => {
    setFormData({
      ...formData,
      [name]: selectedOption ? selectedOption.value : "",
    });
    if (errors[name]) {
      setErrors((prev) => { const e = { ...prev }; delete e[name]; return e; });
    }
  };

  const handleShiftSelectChange = (selectedOption, field) => {
    if (field === "shift") {
      // Only track shift change when editing — during create there is no previous shift
      if (isEdit) {
        setHasShiftChanged(selectedOption.value !== record?.shift?._id);
      }
      setFormData({ ...formData, [field]: selectedOption.value });
    } else {
      setFormData({ ...formData, [field]: selectedOption.value });
    }
    if (errors[field]) {
      setErrors((prev) => { const e = { ...prev }; delete e[field]; return e; });
    }
  };

  const handleAmountBlur = (e, field) => {
    let value = e.target.value.replace(/,/g, "");
    if (!isNaN(value) && value !== "") {
      const formattedValue = parseFloat(value).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
      setFormData((prev) => ({
        ...prev,
        [field]: formattedValue,
      }));
    }
  };

  // Helper: error border style for Select wrappers
  const errBorder = (field) =>
    errors[field]
      ? { border: "1px solid #dc3545", borderRadius: "4px", padding: "1px" }
      : {};

  const validateStep0 = () => {
    const errs = {};
    if (!formData.full_name?.trim()) errs.full_name = "Full Name is required.";
    if (!formData.nic?.trim()) {
      errs.nic = "NIC is required.";
    } else if (!validateNIC(formData.nic)) {
      errs.nic = "Invalid NIC format. Use e.g. 802001754V or 197818601170.";
    }
    if (!formData.gender) errs.gender = "Gender is required.";
    if (formData.email && !validateEmail(formData.email)) errs.email = "Email is not valid.";
    if (formData.contactNumber && !validateSriLankaPhoneNumber(formData.contactNumber))
      errs.contactNumber = "Phone must be 10 digits starting with 0.";
    if (!formData.center) errs.center = "Center is required.";
    if (!formData.branch) errs.branch = "Branch is required.";
    if (!formData.departmentId) errs.departmentId = "Department is required.";
    if (!formData.designationId) errs.designationId = "Designation is required.";
    if (!formData.joiningDate) errs.joiningDate = "Joining Date is required.";
    if (!formData.shift) errs.shift = "Shift is required.";
    if (["Contract", "Probation"].includes(formData.employeeCategory)) {
      if (!formData.probationStart) errs.probationStart = "Probation Start Date is required.";
      if (!formData.probationEnd) errs.probationEnd = "Probation End Date is required.";
      else if (formData.probationStart && moment(formData.probationEnd).isBefore(moment(formData.probationStart)))
        errs.probationEnd = "Probation End Date cannot be before Start Date.";
    }
    if (isEdit && hasShiftChanged && !formData.changeDate) errs.changeDate = "Effective Date is required for shift change.";
    if (!formData.employeeType) errs.employeeType = "Employee Type is required.";
    if (!formData.payFrequency) errs.payFrequency = "Pay Frequency is required.";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const validateStep1 = () => {
    const errs = {};
    if (formData.emergencyNo1 && !validateSriLankaPhoneNumber(formData.emergencyNo1))
      errs.emergencyNo1 = "Emergency No 1 must be 10 digits starting with 0.";
    if (formData.emergencyNo2 && !validateSriLankaPhoneNumber(formData.emergencyNo2))
      errs.emergencyNo2 = "Emergency No 2 must be 10 digits starting with 0.";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const validateStep2 = () => {
    const errs = {};
    if (
      formData.employeeCategory === "Full Time" &&
      (!formData.basicSalary || parseFloat(formData.basicSalary.toString().replace(/,/g, "")) <= 0)
    ) {
      errs.basicSalary = "Basic Salary is required and must be > 0 for Full Time employees.";
    }
    if (errors.epfNumber) {
      errs.epfNumber = errors.epfNumber;
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const nextStep = () => {
    let isValid = false;
    if (currentStep === 0) {
      isValid = validateStep0();
    } else if (currentStep === 1) {
      isValid = validateStep1();
    } else if (currentStep === 2) {
      isValid = validateStep2();
    } else {
      isValid = true;
    }

    if (isValid) {
      setErrors({});
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    setErrors({});
    setCurrentStep(currentStep - 1);
  };

  const handleCancel = () => {
    setCurrentStep(0);
    setErrors({});
    onClose();
  };

  // File upload handlers
  const handleChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFileList([file]);
      try {
        const previewUrl = URL.createObjectURL(file);
        setUploadedPic(previewUrl);
      } catch (error) {
        console.error("Error creating preview URL:", error);
      }
    } else {
      setFileList([]);
      setUploadedPic(isEdit && formData.profile_image_url ? formData.profile_image_url : "");
    }
  };

  const renderFileIcon = (file) => {
    const fileType = file.name.split(".").pop().toLowerCase();
    if (fileType === "pdf") {
      return (
        <AiFillFilePdf
          style={{ fontSize: 24, color: "red", cursor: "pointer" }}
          onClick={() => handlePreview2(file, "pdf")}
        />
      );
    } else if (fileType === "doc" || fileType === "docx") {
      return (
        <FaFileWord
          style={{ fontSize: 24, color: "blue" }}
          onClick={() => handlePreview2(file, "doc")}
        />
      );
    } else {
      return <p>{file.name}</p>;
    }
  };

  const handlePreview2 = (file, type) => {
    const fileUrl = file.url || URL.createObjectURL(file.originFileObj);
    setPreviewFile(fileUrl);
    if (type === "doc") return;
    setPreviewVisible(true);
  };

  // Steps configuration
  const steps = [
    {
      title: "Employee Information",
      content: (
        <div className="row">


          <div className="card-title-head">
            <h6>Personal Details</h6>
          </div>

          {/* Profile Picture Upload */}
          <div className="col-lg-2 col-md-3">
            <div className="profile-pic-upload">
              {isEdit && (
                <div style={{ textAlign: "center", marginBottom: "20px" }}>
                  <label className="form-label" style={{ display: "block", marginBottom: "10px" }}>
                    Current Profile Picture
                  </label>
                  <Image
                    src={uploadedPic || "/placeholder.svg"}
                    alt="Profile Picture"
                    width={120}
                    height={120}
                    style={{
                      objectFit: "cover",
                      borderRadius: "50%",
                      border: "2px solid #ddd",
                      padding: "5px",
                      boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                    }}
                    preview={{
                      zIndex: 10000,
                      visible: previewOpen,
                      src: previewImage,
                      onVisibleChange: (visible) => setPreviewOpen(visible),
                    }}
                    onClick={() => {
                      setPreviewImage(uploadedPic);
                      setPreviewOpen(true);
                    }}
                  />
                </div>
              )}

              <div className="input-blocks mb-0">
                <label className="form-label" style={{ marginLeft: "6px" }}>
                  {isEdit ? "Upload New " : ""}Profile Picture
                </label>
                <input
                  type="file"
                  accept="image/*"
                  className="form-control"
                  style={{ marginTop: '10px' }}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>

          {/* Title */}
          <div className="col-lg-2 col-md-3">
            <div className="mb-3">
              <label className="form-label">Title</label>
              <Select
                value={
                  formData.title === ""
                    ? null
                    : titleOptions.find((option) => formData.title.includes(option.value))
                }
                options={titleOptions}
                className="select"
                placeholder="Select Title"
                onChange={(selectedOption) => handleSelectChange(selectedOption, "title")}
                style={{ width: "100%", height: "40px" }}
              />
            </div>
          </div>

          {/* Full Name */}
          <div className="col-lg-4 col-md-6">
            <div className="mb-3">
              <label className="form-label">Full Name *</label>
              <input
                type="text"
                className={`form-control${errors.full_name ? " is-invalid" : ""}`}
                value={formData.full_name}
                onChange={(e) => handleInputChange(e, "full_name")}
                style={{ width: "100%", height: "38px" }}
                placeholder="Enter Full Name"
                required
              />
              {errors.full_name && <div className="invalid-feedback">{errors.full_name}</div>}
            </div>
          </div>

          {/* NIC */}
          <div className="col-lg-4 col-md-6">
            <div className="mb-3">
              <label className="form-label">NIC *</label>
              <input
                type="text"
                className={`form-control${errors.nic ? " is-invalid" : ""}`}
                value={formData.nic}
                onChange={(e) => handleInputChange(e, "nic")}
                style={{ width: "100%", height: "38px" }}
                placeholder="Enter NIC"
                required
                name="nic_field_unique"
                autoComplete="off"
              />
              {errors.nic && <div className="invalid-feedback">{errors.nic}</div>}
            </div>
          </div>

          {/* Date of Birth */}
          <div className="col-lg-4 col-md-6">
            <div className="mb-3">
              <label className="form-label">Date of Birth</label>
              <div className="input-groupicon calender-input">
                <DatePicker
                  selected={formData.dateOfBirth ? new Date(formData.dateOfBirth) : null}
                  onChange={(date) =>
                    handleDateChange(date, date ? moment(date).format("YYYY-MM-DD") : null, "dateOfBirth")
                  }
                  className="form-control"
                  placeholderText="Select Date of Birth"
                  dateFormat="yyyy-MM-dd"
                  style={{ width: "100%", height: "38px" }}
                  popperModifiers={{
                    preventOverflow: {
                      enabled: true,
                      boundariesElement: "viewport",
                    },
                    offset: {
                      enabled: true,
                      offset: "0px, 10px",
                    },
                  }}
                />
              </div>
            </div>
          </div>
          {/* Gender */}
          <div className="col-lg-4 col-md-6">
            <div className="mb-3">
              <label className="form-label">Gender *</label>
              <div style={errBorder("gender")}>
                <Select
                  name="gender"
                  value={gender.find((option) => option.value.toLowerCase() === formData.gender?.toLowerCase())}
                  onChange={(selectedOption) => handleSelectChange(selectedOption, "gender")}
                  className="select"
                  options={gender}
                  placeholder="Select Gender"
                />
              </div>
              {errors.gender && <div className="invalid-feedback" style={{ display: "block" }}>{errors.gender}</div>}
            </div>
          </div>

          <div className="col-lg-4 col-md-6">
            <div className="mb-3">
              <label className="form-label">Marital Status</label>
              <Select
                name="marital_status"
                value={
                  formData.marital_status
                    ? { value: formData.marital_status, label: formData.marital_status }
                    : null
                }
                onChange={(selectedOption) => handleSelectChange(selectedOption, "marital_status")}
                className="select"
                options={[
                  { value: "Single", label: "Single" },
                  { value: "Married", label: "Married" },
                ]}
                placeholder="Select Marital Status"
              />
            </div>
          </div>

          {/* Nationality */}
          <div className="col-lg-4 col-md-6">
            <div className="mb-3">
              <label className="form-label">Nationality</label>
              <Select
                name="nationality"
                value={nationalitiesOptions.find(
                  (option) => option.value === formData.nationality
                )}
                onChange={(selectedOption) => handleSelectChange(selectedOption, "nationality")}
                className="select"
                options={nationalitiesOptions}
                placeholder="Select Nationality"
              />
            </div>
          </div>

          {/* Contact Details Section */}
          <div className="card-title-head" style={{ marginTop: "15px" }}>
            <h6>Contact & Other Details</h6>
          </div>

          {/* Email */}
          <div className="col-lg-4 col-md-6">
            <div className="mb-3">
              <label className="form-label">Email</label>
              <input
                type="email"
                className={`form-control${errors.email ? " is-invalid" : ""}`}
                value={formData.email}
                onChange={(e) => handleInputChange(e, "email")}
                placeholder="Enter Email"
                style={{ width: "100%", height: "40px" }}
              />
              {errors.email && <div className="invalid-feedback">{errors.email}</div>}
            </div>
          </div>

          {/* Phone Number */}
          <div className="col-lg-4 col-md-6">
            <div className="mb-3">
              <label className="form-label">Phone Number</label>
              <input
                type="text"
                className={`form-control${errors.contactNumber ? " is-invalid" : ""}`}
                value={formData.contactNumber}
                onChange={(e) => handleInputChange(e, "contactNumber")}
                placeholder="Enter Phone Number"
                style={{ width: "100%", height: "40px" }}
              />
              {errors.contactNumber && <div className="invalid-feedback">{errors.contactNumber}</div>}
            </div>
          </div>

          <div className="col-lg-4 col-md-6">
            <div className="mb-3">
              <label className="form-label">Center *</label>
              <div style={errBorder("center")}>
                <Select
                  value={
                    formData.center === ""
                      ? null
                      : center.find((option) => option.value === formData.center)
                  }
                  options={center}
                  className="select"
                  placeholder="Select Center"
                  onChange={(selectedOption) => handleSelectChange(selectedOption, "center")}
                  style={{ width: "100%", height: "40px" }}
                  required
                />
              </div>
              {errors.center && <div className="invalid-feedback" style={{ display: "block" }}>{errors.center}</div>}
            </div>
          </div>

          {/* Branch */}
          <div className="col-lg-4 col-md-6">
            <div className="mb-3">
              <label className="form-label">Branch *</label>
              <div style={errBorder("branch")}>
                <Select
                  value={
                    formData.branch === ""
                      ? null
                      : branchOptions.find((option) =>
                        option.value.toLowerCase() === formData.branch.toLowerCase()
                      )
                  }
                  options={branchOptions}
                  className="select"
                  onChange={(selectedOption) => handleSelectChange(selectedOption, "branch")}
                  placeholder="Select Branch"
                  autoComplete="off"
                  inputId="branch-select-input"
                />
              </div>
              {errors.branch && <div className="invalid-feedback" style={{ display: "block" }}>{errors.branch}</div>}
            </div>
          </div>

          {/* Department */}
          <div className="col-lg-4 col-md-6">
            <div className="mb-3">
              <label className="form-label">Department *</label>
              <select
                className={`form-select${errors.departmentId ? " is-invalid" : ""}`}
                value={formData.departmentId}
                onChange={(e) => handleInputChange(e, "departmentId")}
                required
              >
                <option value="">Select Department</option>
                {departments &&
                  departments.map((dep, index) => (
                    <option key={index} value={dep._id}>
                      {dep.departmentName}
                    </option>
                  ))}
              </select>
              {errors.departmentId && <div className="invalid-feedback">{errors.departmentId}</div>}
            </div>
          </div>

          {/* Designation */}
          <div className="col-lg-4 col-md-6">
            <div className="mb-3">
              <label className="form-label">Designation *</label>
              <select
                className={`form-select${errors.designationId ? " is-invalid" : ""}`}
                value={formData.designationId}
                onChange={(e) => handleInputChange(e, "designationId")}
                required
              >
                <option value="">Select Designation</option>
                {designations &&
                  designations.map((des, index) => (
                    <option key={index} value={des._id}>
                      {des.designationName}
                    </option>
                  ))}
              </select>
              {errors.designationId && <div className="invalid-feedback">{errors.designationId}</div>}
            </div>
          </div>

          {/* Joining Date */}
          <div className="col-lg-4 col-md-6">
            <div className="mb-3">
              <label className="form-label">Joining Date *</label>
              <div className="input-groupicon calender-input">
                <DatePicker
                  selected={formData.joiningDate ? new Date(formData.joiningDate) : null}
                  onChange={(date) =>
                    handleDateChange(date, date ? moment(date).format("YYYY-MM-DD") : null, "joiningDate")
                  }
                  className={`form-control${errors.joiningDate ? " is-invalid" : ""}`}
                  placeholderText="Select Joining Date"
                  dateFormat="yyyy-MM-dd"
                  style={{ width: "100%", height: "38px", borderColor: errors.joiningDate ? "#dc3545" : undefined }}
                  popperModifiers={{
                    preventOverflow: {
                      enabled: true,
                      boundariesElement: "viewport",
                    },
                    offset: {
                      enabled: true,
                      offset: "0px, 10px",
                    },
                  }} required
                />
              </div>
              {errors.joiningDate && <div className="invalid-feedback" style={{ display: "block" }}>{errors.joiningDate}</div>}
            </div>
          </div>

          <div className="col-lg-4 col-md-6">
            <div className="mb-3">
              <label className="form-label">Employee Category</label>
              <Select
                value={
                  formData.employeeCategory === ""
                    ? null
                    : employeeCategoryOptions.find(
                      (option) => option.value === formData.employeeCategory
                    )
                }
                options={employeeCategoryOptions}
                className="select"
                onChange={(selectedOption) =>
                  handleSelectChange(selectedOption, "employeeCategory")
                }
                placeholder="Select Employee Category"
              />
            </div>
          </div>

          {/* Probation Start and End Dates (Conditional) */}
          {["Contract", "Probation"].includes(formData.employeeCategory) && (
            <>
              <div className="col-lg-4 col-md-6">
                <div className="mb-3">
                  <label className="form-label">Probation Start Date *</label>
                  <div className="input-groupicon calender-input">
                    <DatePicker
                      selected={formData.probationStart ? new Date(formData.probationStart) : null}
                      onChange={(date) =>
                        handleDateChange(date, date ? moment(date).format("YYYY-MM-DD") : null, "probationStart")
                      }
                      className="form-control"
                      placeholderText="Select Probation Start Date"
                      dateFormat="yyyy-MM-dd"
                      style={{ width: "100%", height: "38px" }}
                      popperModifiers={{
                        preventOverflow: {
                          enabled: true,
                          boundariesElement: "viewport", // Ensure the popper stays within the viewport
                        },
                        offset: {
                          enabled: true,
                          offset: "0px, 10px", // Adjust offset if needed
                        },
                      }} required
                    />
                  </div>
                </div>
              </div>
              <div className="col-lg-4 col-md-6">
                <div className="mb-3">
                  <label className="form-label">Probation End Date *</label>
                  <div className="input-groupicon calender-input">
                    <DatePicker
                      selected={formData.probationEnd ? new Date(formData.probationEnd) : null}
                      onChange={(date) =>
                        handleDateChange(date, date ? moment(date).format("YYYY-MM-DD") : null, "probationEnd")
                      }
                      className="form-control"
                      placeholderText="Select Probation End Date"
                      dateFormat="yyyy-MM-dd"
                      style={{ width: "100%", height: "38px" }}
                      popperModifiers={{
                        preventOverflow: {
                          enabled: true,
                          boundariesElement: "viewport", // Ensure the popper stays within the viewport
                        },
                        offset: {
                          enabled: true,
                          offset: "0px, 10px", // Adjust offset if needed
                        },
                      }} required
                    />
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Shift Selection */}
          <div className="col-lg-4 col-md-6">
            <div className="mb-3">
              <label className="form-label">Select Shift *</label>
              <div style={errBorder("shift")}>
                <Select
                  showSearch
                  placeholder="Select Shift"
                  value={shifts.find((option) => option.value === formData.shift)}
                  onChange={(selectedOption) => handleShiftSelectChange(selectedOption, "shift")}
                  options={shifts}
                  style={{ width: "100%", height: "40px" }}
                />
              </div>
              {errors.shift && <div className="invalid-feedback" style={{ display: "block" }}>{errors.shift}</div>}
            </div>
          </div>

          {/* Shift Change Fields (if shift changed) */}
          {isEdit && hasShiftChanged && (
            <>
              <div className="col-lg-4 col-md-6">
                <div className="mb-3">
                  <label className="form-label">Effective Date *</label>
                  <div className="input-groupicon calender-input">
                    <DatePicker
                      selected={formData.changeDate ? new Date(formData.changeDate) : null}
                      onChange={(date) =>
                        handleDateChange(date, date ? moment(date).format("YYYY-MM-DD") : null, "changeDate")
                      }
                      className="form-control"
                      placeholderText="Select Effective Date"
                      dateFormat="yyyy-MM-dd"
                      style={{ width: "100%", height: "38px" }}
                      popperModifiers={{
                        preventOverflow: {
                          enabled: true,
                          boundariesElement: "viewport", // Ensure the popper stays within the viewport
                        },
                        offset: {
                          enabled: true,
                          offset: "0px, 10px", // Adjust offset if needed
                        },
                      }} required
                    />
                  </div>
                </div>
              </div>
              <div className="col-lg-4 col-md-6">
                <div className="mb-3">
                  <label className="form-label">Shift Change Reason (Optional)</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.shiftChangeReason}
                    onChange={(e) => handleInputChange(e, "shiftChangeReason")}
                    placeholder="Enter Shift Change Reason"
                    style={{ width: "100%", height: "38px" }}
                  />
                </div>
              </div>
            </>
          )}

          {/* Employee Type */}
          <div className="col-lg-4 col-md-6">
            <div className="mb-3">
              <label className="form-label">Employee Type *</label>
              <div style={errBorder("employeeType")}>
                <Select
                  value={employeeTypes.find((option) => option.value === formData.employeeType)}
                  onChange={(selectedOption) => handleSelectChange(selectedOption, "employeeType")}
                  className="select"
                  options={employeeTypes}
                  placeholder="Select Employee Type"
                />
              </div>
              {errors.employeeType && <div className="invalid-feedback" style={{ display: "block" }}>{errors.employeeType}</div>}
            </div>
          </div>

          {/* Pay Frequency */}
          <div className="col-lg-4 col-md-6">
            <div className="mb-3">
              <label className="form-label">Pay Frequency *</label>
              <div style={errBorder("payFrequency")}>
                <Select
                  name="payFrequency"
                  value={payFrequencies.find(
                    (option) => option.value === formData.payFrequency
                  )}
                  onChange={(selectedOption) => handleSelectChange(selectedOption, "payFrequency")}
                  className="select"
                  options={payFrequencies}
                  placeholder="Select Pay Frequency"
                />
              </div>
              {errors.payFrequency && <div className="invalid-feedback" style={{ display: "block" }}>{errors.payFrequency}</div>}
            </div>
          </div>

          {/* Checkboxes */}
          <div className="d-flex">
            <div className="col-lg-2 col-md-6">
              <div className="mb-3">
                <Checkbox
                  checked={formData.isTopLevel}
                  onChange={(e) => {
                    setFormData((prev) => ({
                      ...prev,
                      isTopLevel: e.target.checked,
                    }));
                  }}
                >
                  <p style={{ color: "black" }}>Upper-Tier Employee</p>
                </Checkbox>
              </div>
            </div>

            <div className="col-lg-2 col-md-6">
              <div className="mb-3">
                <Checkbox
                  checked={formData.isRosterShift}
                  onChange={(e) => {
                    setFormData((prev) => ({
                      ...prev,
                      isRosterShift: e.target.checked,
                    }));
                  }}
                >
                  <p style={{ color: "black" }}>Roster Shift</p>
                </Checkbox>
              </div>
            </div>
          </div>
        </div>
      ),
    },
    {
      title: "Bank & Other Information",
      content: (
        <div className="row">
          {/* Bank Information Section */}
          <div className="card-title-head">
            <h6>Bank Information</h6>
          </div>

          <div className="col-lg-6 col-md-6">
            <div className="mb-3">
              <label className="form-label">Account Holder Name</label>
              <input
                type="text"
                className="form-control"
                value={formData.accountHolderName}
                onChange={(e) => handleInputChange(e, "accountHolderName")}
                placeholder="Enter Account Holder Name"
                style={{ width: "100%", height: "40px" }}
                name="account_holder_name_unique"
                autoComplete="new-password"
              />
            </div>
          </div>

          <div className="col-lg-6 col-md-6">
            <div className="mb-3">
              <label className="form-label">Account Number</label>
              <input
                type="text"
                className="form-control"
                value={formData.accountNumber}
                onChange={(e) => handleInputChange(e, "accountNumber")}
                placeholder="Enter Account Number"
                style={{ width: "100%", height: "40px" }}
                name="account_number_unique"
                autoComplete="new-password"
              />
            </div>
          </div>

          <div className="col-lg-6 col-md-6">
            <div className="mb-3">
              <label className="form-label">Bank Name</label>
              <input
                type="text"
                className="form-control"
                value={formData.bankName}
                onChange={(e) => handleInputChange(e, "bankName")}
                placeholder="Enter Bank Name"
                style={{ width: "100%", height: "40px" }}
                name="bank_name_unique"
                autoComplete="new-password"
              />
            </div>
          </div>

          <div className="col-lg-6 col-md-6">
            <div className="mb-3">
              <label className="form-label">Branch Location</label>
              <input
                type="text"
                className="form-control"
                value={formData.branchLocation}
                onChange={(e) => handleInputChange(e, "branchLocation")}
                placeholder="Enter Branch Location"
                style={{ width: "100%", height: "40px" }}
                name="branch_location_unique_random_field"
                autoComplete="new-password"
              />
            </div>
          </div>

          {/* Other Information Section */}
          <div className="card-title-head" style={{ marginTop: "15px" }}>
            <h6>Other Information</h6>
          </div>


          <div className="col-lg-6 col-md-6">
            <div className="mb-3">
              <label className="form-label">Emergency Contact Person</label>
              <input
                type="text"
                className="form-control"
                value={formData.emergency_name}
                onChange={(e) => handleInputChange(e, "emergency_name")}
                placeholder="Enter Emergency Contact Person"
                style={{ width: "100%", height: "40px" }}
              />
            </div>
          </div>

          <div className="col-lg-6 col-md-6">
            <div className="mb-3">
              <label className="form-label">Relationship</label>
              <input
                type="text"
                className="form-control"
                value={formData.emergency_relationship}
                onChange={(e) => handleInputChange(e, "emergency_relationship")}
                placeholder="Enter Relationship"
                style={{ width: "100%", height: "40px" }}
              />
            </div>
          </div>

          <div className="col-lg-6 col-md-6">
            <div className="mb-3">
              <label className="form-label">Emergency No 1</label>
              <input
                type="text"
                className={`form-control${errors.emergencyNo1 ? " is-invalid" : ""}`}
                value={formData.emergencyNo1}
                onChange={(e) => handleInputChange(e, "emergencyNo1")}
                placeholder="Enter Emergency No 1"
                style={{ width: "100%", height: "40px" }}
              />
              {errors.emergencyNo1 && <div className="invalid-feedback" style={{ display: "block" }}>{errors.emergencyNo1}</div>}
            </div>
          </div>

          <div className="col-lg-6 col-md-6">
            <div className="mb-3">
              <label className="form-label">
                Emergency No 2{" "}
                <span style={{ color: "#C0C0C0" }}>(Optional)</span>
              </label>
              <input
                type="text"
                className={`form-control${errors.emergencyNo2 ? " is-invalid" : ""}`}
                value={formData.emergencyNo2}
                onChange={(e) => handleInputChange(e, "emergencyNo2")}
                placeholder="Enter Emergency No 2"
                style={{ width: "100%", height: "40px" }}
              />
              {errors.emergencyNo2 && <div className="invalid-feedback" style={{ display: "block" }}>{errors.emergencyNo2}</div>}
            </div>
          </div>

          <div className="col-lg-6 col-md-6">
            <div className="mb-3">
              <label className="form-label">Permanent Address</label>
              <input
                type="text"
                className="form-control"
                value={formData.address}
                onChange={(e) => handleInputChange(e, "address")}
                placeholder="Enter Permanent Address"
                style={{ width: "100%", height: "40px" }}
              />
            </div>
          </div>

          <div className="col-lg-6 col-md-6">
            <div className="mb-3">
              <label className="form-label">Residential Address</label>
              <input
                type="text"
                className="form-control"
                value={formData.residenceAddress}
                onChange={(e) => handleInputChange(e, "residenceAddress")}
                placeholder="Enter Residential Address"
                style={{ width: "100%", height: "40px" }}
              />
            </div>
          </div>

          <div className="col-lg-6 col-md-6">
            <div className="mb-3">
              <label className="form-label">City</label>
              <input
                type="text"
                className="form-control"
                value={formData.city}
                onChange={(e) => handleInputChange(e, "city")}
                placeholder="Enter City"
                style={{ width: "100%", height: "40px" }}
              />
            </div>
          </div>

          <div className="col-lg-6 col-md-6">
            <div className="mb-3">
              <label className="form-label">Province</label>
              <Select
                name="province"
                value={sriLankaProvinces.find(
                  (option) => option.value === formData.province
                )}
                onChange={(selectedOption) => handleSelectChange(selectedOption, "province")}
                className="select"
                options={sriLankaProvinces}
                placeholder="Select Province"
                style={{ width: "100%", height: "40px" }}
                autoComplete="off"
                inputId="province-select-input"
              />
            </div>
          </div>
        </div>
      ),
    },
    {
      title: "Compensation",
      content: (
        <div>
          {/* EPF, Salary, Tax, OT (for all categories, basic salary is required only for Full Time) */}
          <div className="row" style={{ marginBottom: "20px", border: "1px solid #e0e0e0", borderRadius: "8px", padding: "16px", background: "#fafafa" }}>
            <div className="card-title-head" style={{ marginBottom: "12px" }}>
              <h6 style={{ fontWeight: "600", fontSize: "15px", margin: 0 }}>Compensation & Benefits Info</h6>
            </div>
            <div className="col-lg-3 col-md-6">
              <div className="mb-3">
                <label className="form-label" style={{ fontSize: "13px" }}>EPF Number</label>
                <input
                  type="text"
                  className={`form-control${errors.epfNumber ? " is-invalid" : ""}`}
                  value={formData.epfNumber}
                  placeholder="Enter EPF Number"
                  onChange={(e) => handleInputChange(e, "epfNumber")}
                />
                {errors.epfNumber && <div className="invalid-feedback" style={{ display: "block" }}>{errors.epfNumber}</div>}
              </div>
            </div>
            <div className="col-lg-3 col-md-6">
              <div className="mb-3">
                <label className="form-label" style={{ fontSize: "13px" }}>Basic Salary {formData.employeeCategory === "Full Time" ? "*" : ""}</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.basicSalary}
                  placeholder="Enter Basic Salary"
                  onWheel={(e) => e.target.blur()}
                  onBlur={(e) => handleAmountBlur(e, "basicSalary")}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/^[0-9,]*\.?[0-9]*$/.test(value)) {
                      handleInputChange(e, "basicSalary");
                    }
                  }}
                />
              </div>
            </div>
            <div className="col-lg-3 col-md-6 d-flex align-items-center">
              <div className="mb-3" style={{ marginTop: "20px" }}>
                <Checkbox
                  checked={formData.payeeTaxApplicable}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, payeeTaxApplicable: e.target.checked }))
                  }
                >
                  <span style={{ color: "black", fontSize: "13px" }}>Payee Tax Applicable</span>
                </Checkbox>
              </div>
            </div>
            <div className="col-lg-3 col-md-6 d-flex align-items-center">
              <div className="mb-3" style={{ marginTop: "20px" }}>
                <Checkbox
                  checked={formData.overtimeEligibility}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, overtimeEligibility: e.target.checked }))
                  }
                >
                  <span style={{ color: "black", fontSize: "13px" }}>Overtime Eligibility</span>
                </Checkbox>
              </div>
            </div>
          </div>

          <div style={{ marginBottom: "8px" }}>
            <h6 style={{ fontWeight: "600", fontSize: "16px", color: "#333" }}>
              Compensation (Allowances & Deductions)
            </h6>
            <p style={{ color: "#888", fontSize: "13px", marginBottom: "0" }}>
              Select applicable allowances and deductions for this employee. These will be applied in payroll calculations.
            </p>
          </div>

          {/* ====== ALLOWANCES SECTION ====== */}
          <div style={{ border: "1px solid #e0e0e0", borderRadius: "8px", padding: "16px", marginBottom: "20px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
              <h6 style={{ fontWeight: "600", fontSize: "15px", margin: 0 }}>Allowances</h6>
              <Button
                type="default"
                size="small"
                style={{ borderColor: "#ff9500", color: "#ff9500" }}
                onClick={() => setShowNewAllowanceCat(!showNewAllowanceCat)}
              >
                + New Allowance Category
              </Button>
            </div>

            {/* Inline New Allowance Category Form */}
            {showNewAllowanceCat && (
              <div style={{ background: "#fdf8f0", border: "1px solid #ffe0b2", borderRadius: "6px", padding: "12px", marginBottom: "12px" }}>
                <div className="row">
                  <div className="col-md-4">
                    <label className="form-label" style={{ fontSize: "13px" }}>Category Name *</label>
                    <input
                      type="text"
                      className="form-control"
                      value={newAllowanceCatName}
                      onChange={(e) => setNewAllowanceCatName(e.target.value)}
                      placeholder="Enter category name"
                      style={{ height: "36px" }}
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label" style={{ fontSize: "13px" }}>Type *</label>
                    <Select
                      value={{ value: newAllowanceCatType, label: newAllowanceCatType }}
                      options={[
                        { value: "Fixed", label: "Fixed" },
                        { value: "Variable", label: "Variable" },
                      ]}
                      onChange={(opt) => setNewAllowanceCatType(opt.value)}
                      className="select"
                      placeholder="Select Type"
                    />
                  </div>
                  <div className="col-md-5 d-flex align-items-end" style={{ gap: "8px" }}>
                    <Button type="primary" size="small" onClick={handleCreateAllowanceCategory}>Save</Button>
                    <Button size="small" onClick={() => { setShowNewAllowanceCat(false); setNewAllowanceCatName(""); }}>Cancel</Button>
                  </div>
                </div>
              </div>
            )}

            {/* Allowance Input Form */}
            <div className="row" style={{ marginBottom: "12px" }}>
              <div className="col-md-3">
                <label className="form-label" style={{ fontSize: "13px" }}>Allowance Category</label>
                <Select
                  value={allowanceCategory}
                  options={(allowances || []).map((a) => ({ value: a.value || a._id, label: a.label || a.name }))}
                  onChange={(opt) => setAllowanceCategory(opt)}
                  className="select"
                  placeholder="Select category"
                  isClearable
                />
              </div>
              <div className="col-md-2">
                <label className="form-label" style={{ fontSize: "13px" }}>Amount (LKR)</label>
                <input
                  type="text"
                  className="form-control"
                  value={allowanceAmount}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (/^[0-9,]*\.?[0-9]*$/.test(val)) setAllowanceAmount(val);
                  }}
                  onBlur={(e) => {
                    let val = e.target.value.replace(/,/g, "");
                    if (!isNaN(val) && val !== "") {
                      setAllowanceAmount(parseFloat(val).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
                    }
                  }}
                  style={{ height: "38px" }}
                />
              </div>
              <div className="col-md-3">
                <label className="form-label" style={{ fontSize: "13px" }}>Effective Date</label>
                <DatePicker
                  selected={allowanceDate}
                  onChange={(date) => setAllowanceDate(date)}
                  className="form-control"
                  placeholderText="Select date"
                  dateFormat="yyyy-MM-dd"
                  style={{ width: "100%", height: "38px" }}
                />
              </div>
              <div className="col-md-4 d-flex align-items-end" style={{ gap: "8px" }}>
                <Button size="small" onClick={resetAllowanceForm}>Reset</Button>
                <Button type="primary" size="small" style={{ backgroundColor: "#ff9500", borderColor: "#ff9500" }} onClick={handleAddAllowance}>
                  {editingAllowanceIndex >= 0 ? "Update Allowance" : "Add Allowance"}
                </Button>
              </div>
            </div>

            {/* Allowances Table */}
            {compensationData?.allowances?.length > 0 && (
              <Table
                size="small"
                pagination={false}
                dataSource={compensationData.allowances.map((item, index) => ({ ...item, key: index }))}
                columns={[
                  { title: "#", key: "index", width: 50, render: (_, __, idx) => idx + 1 },
                  { title: "Allowance Category", dataIndex: "allowanceTypeName", key: "allowanceTypeName" },
                  { title: "Amount (LKR)", dataIndex: "amount", key: "amount", render: (val) => val?.toLocaleString("en-US", { minimumFractionDigits: 2 }) },
                  { title: "Effective Date", dataIndex: "effectiveDate", key: "effectiveDate" },
                  {
                    title: "Actions", key: "actions", width: 150,
                    render: (_, __, idx) => (
                      <div style={{ display: "flex", gap: "6px" }}>
                        <Button size="small" type="primary" style={{ backgroundColor: "#ff9500", borderColor: "#ff9500", fontSize: "12px" }} onClick={() => handleEditAllowance(idx)}>Edit</Button>
                        <Button size="small" danger style={{ fontSize: "12px" }} onClick={() => handleDeleteAllowance(idx)}>Delete</Button>
                      </div>
                    ),
                  },
                ]}
              />
            )}
          </div>

          {/* ====== DEDUCTIONS SECTION ====== */}
          <div style={{ border: "1px solid #e0e0e0", borderRadius: "8px", padding: "16px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
              <h6 style={{ fontWeight: "600", fontSize: "15px", margin: 0 }}>Deductions</h6>
              <Button
                type="default"
                size="small"
                style={{ borderColor: "#ff9500", color: "#ff9500" }}
                onClick={() => setShowNewDeductionCat(!showNewDeductionCat)}
              >
                + New Deduction Category
              </Button>
            </div>

            {/* Inline New Deduction Type Form */}
            {showNewDeductionCat && (
              <div style={{ background: "#fdf8f0", border: "1px solid #ffe0b2", borderRadius: "6px", padding: "12px", marginBottom: "12px" }}>
                <div className="row">
                  <div className="col-md-5">
                    <label className="form-label" style={{ fontSize: "13px" }}>Deduction Type Name *</label>
                    <input
                      type="text"
                      className="form-control"
                      value={newDeductionCatName}
                      onChange={(e) => setNewDeductionCatName(e.target.value)}
                      placeholder="Enter deduction type name"
                      style={{ height: "36px" }}
                    />
                  </div>
                  <div className="col-md-5 d-flex align-items-end" style={{ gap: "8px" }}>
                    <Button type="primary" size="small" onClick={handleCreateDeductionType}>Save</Button>
                    <Button size="small" onClick={() => { setShowNewDeductionCat(false); setNewDeductionCatName(""); }}>Cancel</Button>
                  </div>
                </div>
              </div>
            )}

            {/* Deduction Input Form */}
            <div className="row" style={{ marginBottom: "8px" }}>
              <div className="col-md-3">
                <label className="form-label" style={{ fontSize: "13px" }}>Deduction Type</label>
                <Select
                  value={deductionType}
                  options={(deductionTypes || []).map((d) => ({ value: d._id, label: d.deducitionType }))}
                  onChange={(opt) => setDeductionType(opt)}
                  className="select"
                  placeholder="Select type"
                  isClearable
                />
              </div>
              <div className="col-md-2">
                <label className="form-label" style={{ fontSize: "13px" }}>Amount (LKR)</label>
                <input
                  type="text"
                  className="form-control"
                  value={deductionAmount}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (/^[0-9,]*\.?[0-9]*$/.test(val)) setDeductionAmount(val);
                  }}
                  onBlur={(e) => {
                    let val = e.target.value.replace(/,/g, "");
                    if (!isNaN(val) && val !== "") {
                      setDeductionAmount(parseFloat(val).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
                    }
                  }}
                  style={{ height: "38px" }}
                />
              </div>
              <div className="col-md-3">
                <label className="form-label" style={{ fontSize: "13px" }}>Effective Date</label>
                <DatePicker
                  selected={deductionDate}
                  onChange={(date) => setDeductionDate(date)}
                  className="form-control"
                  placeholderText="Select date"
                  dateFormat="yyyy-MM-dd"
                  style={{ width: "100%", height: "38px" }}
                />
              </div>
              <div className="col-md-4 d-flex align-items-end" style={{ gap: "8px" }}>
                <Button size="small" onClick={resetDeductionForm}>Reset</Button>
                <Button type="primary" size="small" style={{ backgroundColor: "#ff9500", borderColor: "#ff9500" }} onClick={handleAddDeduction}>
                  {editingDeductionIndex >= 0 ? "Update Deduction" : "Add Deduction"}
                </Button>
              </div>
            </div>

            {/* Type Radio */}
            <div style={{ marginBottom: "12px" }}>
              <label className="form-label" style={{ fontSize: "13px", marginRight: "12px" }}>Type</label>
              <Radio.Group value={deductionPaymentType} onChange={(e) => setDeductionPaymentType(e.target.value)}>
                <Radio value="oneTime">One Time</Radio>
                <Radio value="recurring">Recurring</Radio>
              </Radio.Group>
            </div>

            {/* Deductions Table */}
            {compensationData?.deductions?.length > 0 && (
              <Table
                size="small"
                pagination={false}
                dataSource={compensationData.deductions.map((item, index) => ({ ...item, key: index }))}
                columns={[
                  { title: "#", key: "index", width: 50, render: (_, __, idx) => idx + 1 },
                  { title: "Deduction Type", dataIndex: "deductionTypeName", key: "deductionTypeName" },
                  { title: "Amount (LKR)", dataIndex: "amount", key: "amount", render: (val) => val?.toLocaleString("en-US", { minimumFractionDigits: 2 }) },
                  { title: "Effective Date", dataIndex: "effectiveDate", key: "effectiveDate" },
                  { title: "Type", dataIndex: "paymentType", key: "paymentType", render: (val) => val === "recurring" ? "Recurring" : "One Time" },
                  {
                    title: "Actions", key: "actions", width: 150,
                    render: (_, __, idx) => (
                      <div style={{ display: "flex", gap: "6px" }}>
                        <Button size="small" type="primary" style={{ backgroundColor: "#ff9500", borderColor: "#ff9500", fontSize: "12px" }} onClick={() => handleEditDeduction(idx)}>Edit</Button>
                        <Button size="small" danger style={{ fontSize: "12px" }} onClick={() => handleDeleteDeduction(idx)}>Delete</Button>
                      </div>
                    ),
                  },
                ]}
              />
            )}
          </div>
        </div>
      ),
    },
    {
      title: "Education & Training",
      content: (
        <div className="row">
          <div className="card-title-head">
            <h6>Education and Training</h6>
          </div>

          {/* Document Upload Section */}
          <div className="row">
            <div className="col-lg-4 col-md-6">
              <div className="mb-3">
                <label className="form-label">Document Type</label>
                <Select
                  options={education_and_training}
                  className="select"
                  placeholder="Select Document Type"
                  onChange={(selectedOption) => {
                    setFormData((prev) => ({
                      ...prev,
                      selectedDocType: selectedOption.value,
                    }));
                  }}
                  value={education_and_training.find(
                    (option) => option.value === formData.selectedDocType
                  )}
                  style={{ width: "100%", height: "40px" }}
                />
              </div>
            </div>
            <div className="col-lg-8 col-md-6">
              <div className="mb-3">
                <label className="form-label">Upload Documents</label>
                <Upload
                  listType="text"
                  fileList={
                    documents
                      .filter((doc) => doc.type === formData.selectedDocType)
                      .map((doc, index) => ({
                        uid: index,
                        name: doc.name,
                        url: doc.url,
                        originFileObj: doc.originFileObj,
                      })) || []
                  }
                  onChange={({ file, fileList }) => {
                    if (fileList.length > 4) {
                      showWarning("You can only upload a maximum of 4 documents!");
                      return;
                    }
                    const fileType = file.name?.split(".").pop().toLowerCase();
                    const isValidType = ["pdf"].includes(fileType);
                    if (file.status !== "removed" && !isValidType) {
                      showWarning("You can only upload PDF files!");
                      return;
                    }

                    const newDocs = fileList.map((f) => ({
                      type: formData.selectedDocType,
                      name: f.name,
                      url: f.url || URL.createObjectURL(f.originFileObj),
                      originFileObj: f.originFileObj,
                    }));

                    setDocuments((prev) => [
                      ...prev.filter((doc) => doc.type !== formData.selectedDocType),
                      ...newDocs,
                    ]);
                  }}
                  multiple={true}
                  showUploadList={{
                    showRemoveIcon: true,
                  }}
                  onPreview={(file) => handlePreview2(file, "pdf")}
                  disabled={!formData.selectedDocType}
                >
                  <Button
                    className="btn-grid-upload"
                    icon={<UploadOutlined className="upload-icon" />}
                    disabled={!formData.selectedDocType}
                  >
                    Upload
                  </Button>
                </Upload>
              </div>
            </div>
          </div>

          {/* Display Uploaded Documents */}
          {documents.length > 0 && (
            <div className="row">
              <Table
                columns={[
                  {
                    title: "Document Type",
                    dataIndex: "type",
                    key: "type",
                    render: (type) =>
                      education_and_training.find((opt) => opt.value === type)?.label || type,
                  },
                  {
                    title: "File Name",
                    dataIndex: "name",
                    key: "name",
                    render: (name, record) => (
                      <div style={{ display: "flex", alignItems: "center" }}>
                        {renderFileIcon({ name, url: record.url })}
                        <p style={{ marginLeft: 8, marginTop: "0px" }}>{name}</p>
                      </div>
                    ),
                  },
                  {
                    title: "Action",
                    key: "action",
                    render: (_, record) => (
                      <DeleteOutlined
                        style={{ color: "red", cursor: "pointer" }}
                        onClick={() => {
                          setDocuments((prev) =>
                            prev.filter(
                              (doc) => doc.name !== record.name || doc.type !== record.type
                            )
                          );
                        }}
                      />
                    ),
                  },
                ]}
                dataSource={documents.map((doc, index) => ({
                  key: index,
                  type: doc.type,
                  name: doc.name,
                  url: doc.url,
                }))}
                rowKey="key"
                pagination={false}
              />
            </div>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <Modal
        zIndex={1200}
        open={isOpen}
        onOk={() => { }}
        width="75vw"
        onCancel={handleCancel}
        footer={null}
        maskClosable={false}
        closable={false}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
          }}
        >
          <div className="modal-header custom-modal-header-grid">
            <div className="page-title">
              <h4>{isEdit ? "Edit" : "Create"} Employee</h4>
              <p className="mb-0">
                Fill in the required fields to {isEdit ? "edit" : "create"} an Employee
              </p>
            </div>
          </div>

          <div className="responsive-icon">
            <X
              style={{
                fontSize: "20px",
                color: "#ff9500",
                cursor: "pointer",
              }}
              onClick={handleCancel}
            />
          </div>
        </div>

        <Steps current={currentStep} style={{ marginBottom: "20px" }}>
          {steps.map((item) => (
            <Step key={item.title} title={item.title} />
          ))}
        </Steps>

        <div
          className="card"
          style={{
            borderRadius: "8px",
            border: "1px solid #e0e0e0",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
          }}
        >
          <div className="card-body" style={{ padding: "20px" }}>
            <div className="new-employee-field">{steps[currentStep].content}</div>
          </div>
        </div>

        <div style={{ marginTop: "20px", textAlign: "right" }}>
          {currentStep > 0 && (
            <Button style={{ marginRight: "8px" }} onClick={prevStep}>
              Previous
            </Button>
          )}
          {currentStep < steps.length - 1 && (
            <Button type="primary" onClick={nextStep}>
              Next
            </Button>
          )}
          {currentStep === steps.length - 1 && (
            <Button type="primary" onClick={handleSubmit} loading={loading} disabled={loading}>
              {isEdit ? "Update" : "Create"} Employee
            </Button>
          )}
        </div>
      </Modal>

      {/* PDF Preview Modal */}
      <Modal
        open={previewVisible}
        zIndex={1200}
        footer={null}
        onCancel={() => setPreviewVisible(false)}
        width={800}
        transitionName=""
      >
        <iframe
          src={previewFile}
          width="100%"
          height="600px"
          style={{ border: "none" }}
          title="PDF Preview"
        />
        <button
          type="button"
          className="btn btn-cancel"
          onClick={() => {
            setPreviewVisible(false);
          }}
        >
          Close
        </button>
      </Modal>
    </>
  );
};

// PropTypes validation
EmployeeModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  isEdit: PropTypes.bool.isRequired,
  formData: PropTypes.object.isRequired,
  setFormData: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  center: PropTypes.array.isRequired,
  titleOptions: PropTypes.array.isRequired,
  gender: PropTypes.array.isRequired,
  branchOptions: PropTypes.array.isRequired,
  departments: PropTypes.array.isRequired,
  designations: PropTypes.array.isRequired,
  shifts: PropTypes.array.isRequired,
  nationalitiesOptions: PropTypes.array.isRequired,
  employeeCategoryOptions: PropTypes.array.isRequired,
  employeeTypes: PropTypes.array.isRequired,
  payFrequencies: PropTypes.array.isRequired,
  sriLankaProvinces: PropTypes.array.isRequired,
  allowances: PropTypes.array,
  education_and_training: PropTypes.array.isRequired,
  uploadedPic: PropTypes.string,
  setUploadedPic: PropTypes.func.isRequired,
  documents: PropTypes.array.isRequired,
  setDocuments: PropTypes.func.isRequired,
  hasShiftChanged: PropTypes.bool.isRequired,
  setHasShiftChanged: PropTypes.func.isRequired,
  record: PropTypes.object,
  fileList: PropTypes.array.isRequired,
  setFileList: PropTypes.func.isRequired,
  maritalStatusOptions: PropTypes.array,
  deductionTypes: PropTypes.array,
  compensationData: PropTypes.object,
  setCompensationData: PropTypes.func,
  onRefreshAllowanceCategories: PropTypes.func,
  onRefreshDeductionTypes: PropTypes.func,
};

export default EmployeeModal;
